import { Grid, Chip, Typography, Card, Box, Button } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { GridColDef, GridRowId, GridRowModel, GridCallbackDetails, GridRowSelectionModel, GridRow } from "@mui/x-data-grid";
import { useParams } from "react-router";
import { useQuery } from "@apollo/client";
import { TablePaginated, TableToolBar, SearchToolbarProps, SearchBar, FetchMoreCallbackArgs, useManageModal } from "ui";
import { GET_PEOPLE_WITH_SESSION_COUNT } from "../../../../features/graphql/query";
import useLocationAssignment from "../hooks/useLocationAssignment";
import { LoadingButton } from "@mui/lab";
const DEFAULT_PAGE_SIZE = 100;
const TABLE_COLUMNS: GridColDef[] = [
    {
        field: "firstName", headerName: "First Name", flex: 1, sortable: true, resizable: false
    }, {
        field: "lastName", headerName: "Last Name", flex: 1, sortable: true, resizable: false
    },
    {
        field: "locations", headerName: "Location(s)", flex: 2, sortable: true, resizable: false,
        renderCell: ({ value }) => {
            const hasLocations = value.length > 0
            if (hasLocations) {
                return (
                    <Grid container spacing={1} sx={{ paddingTop: 2, paddingBottom: 2, justifyContent: "start" }}>
                        {value.map((location: string) => (
                            <Grid key={`tags-chip-${location}`} style={{ marginTop: 1 }}>
                                <Chip className="chip" label={location} />
                            </Grid>
                        ))}
                    </Grid>
                )
            } else {
                return (<Typography>Please assign person to a location</Typography>)
            }
        }
    }
]
function deltaSelections(a: GridRowId[], b: GridRowId[], c: GridRowId[]): GridRowId[] {

    const added = a.filter(x => !b.includes(x));
    const removed = b.filter(x => !a.includes(x));

    const updated = [
        ...c.filter((x: any) => !removed.includes(x)),
        ...added
    ]
    return ([] as GridRowId[])
        .concat(c.filter(x => !removed.includes(x)))
        .concat(added);
}

const unique = <T,>(array: T[]): T[] => {
    return Array.from(new Set(array));
}
function mapToTable(data: any) {
    const node = data.node
    const _data = {
        ...node,
        locations: node.locations?.edges?.map((x: any) => x.node.name),
        sessionsReviewed: node.workLists.filter((x: any) => x.status === "Read").length
    }
    return _data
}
interface ManageStaffTableContainerProps {
    children: React.ReactNode
}
type ActionOnCallback = () => void;

interface ManageStaffTableToolbarProps extends SearchToolbarProps { }

interface ManageStaffTableProps {
    onBack: ActionOnCallback
}

interface PesonTableLinkFooterProps {
    saving: boolean,
    disabled: boolean,
    onBack: ActionOnCallback
    onSave: ActionOnCallback
}

const ManageStaffTable = ({ onBack, locationName }: { onBack: () => void, locationName: string | null }) => {
    const { lid } = useParams()
    const { savePersons } = useLocationAssignment(lid ?? '')
    const { loading, data, fetchMore } = useQuery(GET_PEOPLE_WITH_SESSION_COUNT, {
        notifyOnNetworkStatusChange: true,
        // fetchPolicy: "cache-and-network",
        variables: {
            query: "",
            first: DEFAULT_PAGE_SIZE
        }
    })
    const { openModal } = useManageModal()
    const [pageTableState, setPageTableState] = useState<{
        columns: GridColDef[],
        rows: GridRowModel[],
        selectionModel: GridRowSelectionModel,
        selections: {
            initial: GridRowId[],
            pageAssign: GridRowId[],
            prevAssign: GridRowId[],
            nextAssign: GridRowId[],
            unassign: GridRowId[],
            allSelected: GridRowId[],
        },
    }>({
        columns: [],
        rows: [],
        selectionModel: { type: 'include', ids: new Set() },
        selections: {
            initial: [],
            pageAssign: [], // Contains student IDs that have a relationship with person
            prevAssign: [], // Contains student IDs across page/table
            nextAssign: [], // Contains student IDs used to created a person/student relationship
            unassign: [],   // Contains student IDs used to remove the person/student relationship
            allSelected: [], // Contains student IDs for selections on table/page
        },
    });

    const [saving, setSaving] = useState<boolean>(false);
    const [pageLoading, setPageLoading] = useState<boolean>(false);

    useEffect(() => {
        if (loading || !data) return;
        setPageTableState((prevState) => {
            const columns = [...TABLE_COLUMNS]
            const rows = data?.personStatsPerCompany?.edges.map(mapToTable);
            const selectionModel: GridRowSelectionModel = { type: 'include', ids: new Set<GridRowId>() }
            for (let i = 0; i < (data?.personStatsPerCompany?.edges?.length ?? 0); i++) {
                const personLocations = data?.personStatsPerCompany?.edges ? data?.personStatsPerCompany?.edges[i].node?.locations.edges : null;
                if (!personLocations) continue;
                const personId = data?.personStatsPerCompany?.edges[i].node.id;
                for (let k = 0; k < personLocations.length; k++) {
                    const isAtLocation = lid == personLocations[k].node.id
                    if (isAtLocation) {
                        selectionModel.ids.add(personId)
                    }
                }
            }
            const _ids = Array.from(selectionModel.ids);
            setPageLoading(false)
            return {
                ...prevState,
                columns,
                rows,
                selectionModel,
                selections: {
                    ...prevState.selections,
                    initial: _ids,
                    pageAssign: _ids,
                    prevAssign: unique(prevState.selections.prevAssign.concat(Array.from(selectionModel.ids))),
                    allSelected: Array.from(selectionModel.ids).concat(prevState.selections.nextAssign)
                }
            }
        })
    }, [loading, data])

    const handleSelectionModelChange = (values: GridRowSelectionModel, row: GridCallbackDetails) => {
        // Sometimes when the page is reloaded, the Table/DataGrid component triggers automatically 
        // without any user intervention. This callback function should only trigger
        // if {@see GridControlledStateReasonLookup} actions happen. However,
        // the reason is undefined (it shouldn't be). So in this case
        // we ignore this false called to preserve our selection model
        // if (!row?.reason) return;
        const selectedIds = Array.from(values.ids)
        const prevSelectedIds = Array.from(pageTableState.selectionModel.ids);
        const assign = selectedIds.filter(x => !prevSelectedIds.includes(x));
        const unassign = prevSelectedIds.filter(x => !selectedIds.includes(x));

        const uniqAssign = assign.filter(x => !pageTableState.selections.prevAssign.includes(x));
        setPageTableState(prevState => {

            const nextAssign = unique(deltaSelections(uniqAssign, unassign, prevState.selections.nextAssign));
            const nextUnassign = deltaSelections(unassign, assign, prevState.selections.unassign);
            const selectionModel = {
                ...prevState,
                selectionModel: values,
                selections: {
                    ...prevState.selections,
                    nextAssign,
                    unassign: nextUnassign,
                    allSelected: unique(selectedIds.concat(nextAssign).filter(x => x !== unassign[0]))
                }
            }

            return selectionModel;
        })
    }

    const handleSave = async () => {

        // In theory, this save event shouldn't fire because the save button should be
        // disabled. However, for completeness sake, we exit the event early if 
        // there's nothing to save.
        if (pageTableState.selections.nextAssign.length === 0 && pageTableState.selections.unassign.length === 0)
            return;

        setSaving(true);

        const newPersonLocation = await savePersons(pageTableState.selections.nextAssign, pageTableState.selections.unassign);

        console.log('Attempt to save assignments');

        setPageTableState(prevState => ({
            ...prevState,
            selections: {
                ...prevState.selections,
                pageAssign: [],
                prevAssign: [],
                nextAssign: [],
                unassign: []
            }
        }))

        setSaving(false);

        openModal({
            type: 'SAVED_ASSIGNMENTS',
            cancelButtonLabel: 'Close'
        });
    }

    const handleFetchMore = async (variables: FetchMoreCallbackArgs) => {

        await fetchMore({
            variables: {
                query: variables.query ?? "",
                after: variables.after ?? null,
                first: variables.first ?? DEFAULT_PAGE_SIZE
            }
        }).then((res) => {
            // setPageLoading(false)
            const rows = res?.data?.personStatsPerCompany?.edges?.map(mapToTable) ?? []
            setPageTableState(prevState => ({
                ...prevState,
                rows
            }))

        }).catch(err => {
            console.log('the is an error: ', err)
        })

    }
    return (
        <ManageStaffTable.Container>
            <TablePaginated
                loading={loading}
                renderToolbar={({ searchQuery, onSearch, onSearchClear }) => {
                    return (
                        <TableToolBar
                            title={`Assign/Remove Staff at ${locationName}`}
                            searchQuery={searchQuery}
                            onSearch={onSearch}
                            onBack={onBack}
                            onSearchClear={onSearchClear}
                        />
                    )
                }}
                totalRows={data?.personStatsPerCompany?.totalCount ?? 0}
                pageInfo={data?.personStatsPerCompany?.pageInfo}
                columns={pageTableState.columns}
                rows={pageTableState.rows}
                pageSize={DEFAULT_PAGE_SIZE}
                autoHeight
                disableBoxShadow={true}
                checkboxSelection={true}
                onFetchMore={handleFetchMore}
                slots={{
                    row: (props: any) => {

                        const person = props.row;
                        const selected = pageTableState.selections.initial.includes(props.row.id);
                        const isOnlyContactAtLocation = person.singleStudents?.some(
                            (e: any) => e?.locationId == lid
                        );
                        const disabled = (isOnlyContactAtLocation && selected);

                        const handleDisabledClick = () => {
                            openModal({ type: 'ONLY_STUDENT_REF' })
                        };

                        return (
                            <div
                                style={{
                                    background: disabled ? '#F5F5F5' : 'initial',
                                    color: disabled ? '#777' : 'initial',
                                    cursor: disabled ? 'not-allowed' : 'default'
                                }}
                                onClick={disabled ? handleDisabledClick : undefined}>
                                <GridRow {...props} />
                            </div>
                        )
                    }
                }}
                isRowSelectable={(params) => {
                    const person = params.row;
                    const isExpired = person.expired === true;

                    const edges = person.singleStudents ?? [];

                    const isOnlyContactAtLocation = edges.some(
                        (e: any) => e?.locationId == lid
                    );
                    const selectable = !isExpired && !isOnlyContactAtLocation;

                    return selectable;
                }}

                selectionProps={{
                    rowSelectionModel: { type: 'include', ids: new Set(pageTableState.selections.allSelected) },
                    onRowSelectionModelChange: handleSelectionModelChange
                }}

            />
            <ManageStaffTable.Footer
                disabled={(pageTableState.selections.nextAssign.length === 0 && pageTableState.selections.unassign.length === 0)}
                saving={saving}
                onBack={onBack}
                onSave={handleSave} />
        </ManageStaffTable.Container>
    )
}

ManageStaffTable.TableToolbar = (props: ManageStaffTableToolbarProps) => {
    return (
        <Box component="div">
            <SearchBar {...props} />
        </Box>
    );
}

ManageStaffTable.Container = ({ children }: ManageStaffTableContainerProps): React.ReactElement => {
    return (
        <Card
            elevation={4}
            style={{
                width: "100%",
                height: "auto",
            }}
        >
            <>{children}</>
        </Card>
    )
}

ManageStaffTable.Footer = ({ disabled, saving, onSave, onBack }: PesonTableLinkFooterProps): React.ReactElement => {
    return (
        <Box
            component="div"
            sx={{ padding: "16px", display: "flex", justifyContent: "flex-end" }}
        >
            <Button
                disableElevation
                variant="contained"
                size="large"
                sx={{
                    marginRight: "16px",
                    backgroundColor: "#C4C4C4",
                    "&:hover": {
                        color: "#FFF",
                        backgroundColor: "red.main",
                    },
                }}
                onClick={onBack}
            >
                Cancel
            </Button>
            <LoadingButton
                loading={saving}
                disabled={disabled}
                disableElevation
                variant="contained"
                size="large"
                sx={{
                    backgroundColor: "primary.main",
                    "&:hover": {
                        backgroundColor: "primary.light",
                    },
                }}
                onClick={onSave}
            >
                Save
            </LoadingButton>
        </Box>
    )
}

export default ManageStaffTable
